import { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { Button } from "./Button";
import { FormInput } from "./FormFields";
import { Modal } from "./Modal";
import {
  isOwnCategoryTag,
  isOwnPoi,
  isOwnTag,
  useLibraryStore,
} from "../store/libraryStore";

interface CategoryTagManagerModalProps {
  open: boolean;
  onClose: () => void;
}

export function CategoryTagManagerModal({
  open,
  onClose,
}: CategoryTagManagerModalProps) {
  const allCategories = useLibraryStore((s) => s.categoryTags);
  const allTags = useLibraryStore((s) => s.tags);
  const allPois = useLibraryStore((s) => s.pois);
  const createCategoryTag = useLibraryStore((s) => s.createCategoryTag);
  const updateCategoryTag = useLibraryStore((s) => s.updateCategoryTag);
  const deleteCategoryTag = useLibraryStore((s) => s.deleteCategoryTag);
  const setCategoryTagMembers = useLibraryStore((s) => s.setCategoryTagMembers);
  const reorderCategoryTags = useLibraryStore((s) => s.reorderCategoryTags);
  const categories = useMemo(
    () =>
      allCategories
        .filter(isOwnCategoryTag)
        .sort(
          (a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name),
        ),
    [allCategories],
  );
  const tags = useMemo(
    () => allTags.filter(isOwnTag).sort((a, b) => a.name.localeCompare(b.name)),
    [allTags],
  );
  const pois = useMemo(() => allPois.filter(isOwnPoi), [allPois]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [memberIds, setMemberIds] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");
  const [uncategorizedOnly, setUncategorizedOnly] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selected = categories.find((category) => category.id === selectedId);
  const selectedIndex = categories.findIndex(
    (category) => category.id === selectedId,
  );

  useEffect(() => {
    if (!open) return;
    const next =
      categories.find((category) => category.id === selectedId) ??
      categories[0];
    setSelectedId(next?.id ?? null);
    setName(next?.name ?? "");
    setMemberIds(new Set(next?.tagIds ?? []));
    setConfirmDelete(false);
    setError(null);
  }, [open, categories, selectedId]);

  const selectCategory = (id: string) => {
    const category = categories.find((item) => item.id === id);
    if (!category) return;
    setSelectedId(id);
    setName(category.name);
    setMemberIds(new Set(category.tagIds));
    setSearch("");
    setConfirmDelete(false);
    setError(null);
  };

  const categorizedTagIds = useMemo(
    () => new Set(categories.flatMap((category) => category.tagIds)),
    [categories],
  );
  const filteredTags = useMemo(() => {
    const query = search.trim().toLowerCase();
    return tags.filter(
      (tag) =>
        (!uncategorizedOnly || !categorizedTagIds.has(tag.id)) &&
        (!query || tag.name.toLowerCase().includes(query)),
    );
  }, [search, tags, uncategorizedOnly, categorizedTagIds]);
  const uncategorizedCount = tags.filter(
    (tag) => !categorizedTagIds.has(tag.id),
  ).length;

  const poiCount = useMemo(() => {
    if (!selected) return 0;
    return pois.filter((poi) => poi.tags.some((tagId) => memberIds.has(tagId)))
      .length;
  }, [pois, selected, memberIds]);

  const save = async () => {
    if (!selected || !name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      if (name.trim() !== selected.name) {
        await updateCategoryTag(selected.id, name.trim());
      }
      await setCategoryTagMembers(selected.id, [...memberIds]);
    } catch (saveError) {
      setError((saveError as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const create = async () => {
    if (!newCategoryName.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const id = await createCategoryTag(newCategoryName.trim());
      setNewCategoryName("");
      setSelectedId(id);
    } catch (createError) {
      setError((createError as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const move = async (direction: -1 | 1) => {
    if (!selected) return;
    const target = selectedIndex + direction;
    if (target < 0 || target >= categories.length) return;
    const ids = categories.map((category) => category.id);
    [ids[selectedIndex], ids[target]] = [ids[target], ids[selectedIndex]];
    setSaving(true);
    setError(null);
    try {
      await reorderCategoryTags(ids);
    } catch (moveError) {
      setError((moveError as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!selected || selected.systemKey) return;
    setSaving(true);
    try {
      await deleteCategoryTag(selected.id);
      setSelectedId(null);
      setConfirmDelete(false);
    } catch (deleteError) {
      setError((deleteError as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Category tags"
      icon="account_tree"
      size="xl"
      footer={
        <div className="flex w-full items-center justify-between gap-3">
          <span className="text-xs text-tertiary">
            {uncategorizedCount} uncategorised{" "}
            {uncategorizedCount === 1 ? "tag" : "tags"}
          </span>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
            <Button
              variant="primary"
              onClick={save}
              loading={saving}
              disabled={!selected || !name.trim()}
            >
              Save changes
            </Button>
          </div>
        </div>
      }
    >
      {error && (
        <div className="mb-4 rounded-lg border border-error/25 bg-error-bg p-3 text-sm text-error">
          {error}
        </div>
      )}
      <div className="grid min-h-96 grid-cols-[13rem_minmax(0,1fr)] gap-4 max-sm:grid-cols-1">
        <aside className="flex min-h-0 flex-col rounded-xl border border-border bg-subtle p-2">
          <div className="space-y-1 overflow-y-auto custom-scrollbar">
            {categories.map((category) => (
              <Button
                key={category.id}
                variant="unstyled"
                selected={category.id === selectedId}
                onClick={() => selectCategory(category.id)}
                className={clsx(
                  "flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm",
                  category.id === selectedId
                    ? "bg-white font-semibold text-primary shadow-sm ring-1 ring-border"
                    : "text-secondary hover:bg-white/70",
                )}
              >
                <span
                  className="material-symbols-outlined text-base"
                  aria-hidden="true"
                >
                  account_tree
                </span>
                <span className="min-w-0 flex-1 truncate">{category.name}</span>
                <span className="text-2xs tabular-nums text-tertiary">
                  {category.tagIds.length}
                </span>
              </Button>
            ))}
          </div>
          <div className="mt-auto border-t border-border pt-2">
            <FormInput
              value={newCategoryName}
              onChange={setNewCategoryName}
              placeholder="New category"
            />
            <Button
              variant="secondary"
              size="sm"
              fullWidth
              className="mt-2"
              onClick={create}
              disabled={!newCategoryName.trim() || saving}
            >
              Add category
            </Button>
          </div>
        </aside>

        {selected ? (
          <section className="min-w-0">
            <div className="mb-3 flex items-end gap-2">
              <div className="min-w-0 flex-1">
                <label className="mb-1 block text-xs font-semibold text-secondary">
                  Category name
                </label>
                <FormInput value={name} onChange={setName} />
              </div>
              <Button
                variant="icon"
                icon="arrow_upward"
                aria-label="Move category up"
                onClick={() => move(-1)}
                disabled={saving || selectedIndex <= 0}
              />
              <Button
                variant="icon"
                icon="arrow_downward"
                aria-label="Move category down"
                onClick={() => move(1)}
                disabled={saving || selectedIndex >= categories.length - 1}
              />
            </div>
            <div className="mb-3 flex items-center justify-between gap-3">
              <p className="text-xs text-tertiary">
                {memberIds.size} tags · {poiCount} matching POIs
              </p>
              {!selected.systemKey &&
                (confirmDelete ? (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-error">
                      Delete this category?
                    </span>
                    <Button variant="danger" size="sm" onClick={remove}>
                      Delete
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setConfirmDelete(false)}
                    >
                      Keep
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setConfirmDelete(true)}
                  >
                    Delete category
                  </Button>
                ))}
            </div>
            <div className="flex items-center gap-2">
              <div className="min-w-0 flex-1">
                <FormInput
                  value={search}
                  onChange={setSearch}
                  placeholder="Search tags"
                />
              </div>
              <Button
                variant="secondary"
                size="sm"
                selected={uncategorizedOnly}
                aria-pressed={uncategorizedOnly}
                onClick={() => setUncategorizedOnly((current) => !current)}
              >
                Uncategorised {uncategorizedCount}
              </Button>
            </div>
            <div className="mt-2 max-h-64 space-y-1 overflow-y-auto rounded-xl border border-border p-2 custom-scrollbar">
              {filteredTags.map((tag) => {
                const checked = memberIds.has(tag.id);
                return (
                  <Button
                    key={tag.id}
                    variant="unstyled"
                    selected={checked}
                    onClick={() =>
                      setMemberIds((current) => {
                        const next = new Set(current);
                        if (checked) next.delete(tag.id);
                        else next.add(tag.id);
                        return next;
                      })
                    }
                    className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm text-primary hover:bg-subtle"
                  >
                    <span
                      className={clsx(
                        "material-symbols-outlined text-lg",
                        checked ? "text-primary" : "text-tertiary",
                      )}
                      aria-hidden="true"
                    >
                      {checked ? "check_box" : "check_box_outline_blank"}
                    </span>
                    {tag.name}
                  </Button>
                );
              })}
              {filteredTags.length === 0 && (
                <p className="px-2 py-4 text-center text-xs text-tertiary">
                  No matching tags.
                </p>
              )}
            </div>
          </section>
        ) : (
          <div className="flex items-center justify-center text-sm text-tertiary">
            Create a category to group related tags.
          </div>
        )}
      </div>
    </Modal>
  );
}
